import { LayoutGridIcon, ListIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import type { ProjectTaskBoard } from "./useProjectTaskBoard";
import type { ProjectTaskViewProps } from "./taskViewTypes";
import { ProjectTaskSurfaceBody } from "./ProjectTaskSurfaceBody";

/** The two layouts this panel offers. Timeline stays a project-surface concern. */
export type ChatTaskBoardLayout = "board" | "list";

const LAYOUTS: ReadonlyArray<{
  readonly id: ChatTaskBoardLayout;
  readonly label: string;
  readonly Icon: typeof ListIcon;
}> = [
  { id: "board", label: "Board", Icon: LayoutGridIcon },
  { id: "list", label: "Table", Icon: ListIcon },
];

export interface ChatTaskBoardPaneProps {
  readonly layout: ChatTaskBoardLayout;
  readonly onLayoutChange: (layout: ChatTaskBoardLayout) => void;
  readonly board: ProjectTaskBoard;
  readonly viewProps: ProjectTaskViewProps;
  readonly projectTitle: string;
}

/**
 * Fallback surface for a thread with no task anchor.
 *
 * A thread that is not on a task is still on a project, so the useful answer is
 * that project's work rather than a dead end. {@link ProjectTaskSurfaceBody} is
 * the same renderer the project surface uses, so board and table stay identical
 * to the ones users already know, including their loading, error and retry
 * states.
 */
export function ChatTaskBoardPane(props: ChatTaskBoardPaneProps) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
        <p className="min-w-0 truncate text-sm font-medium text-foreground">{props.projectTitle}</p>
        <div className="flex shrink-0 items-center gap-0.5 rounded-lg bg-muted/40 p-0.5">
          {LAYOUTS.map((layout) => (
            <button
              key={layout.id}
              type="button"
              aria-label={layout.label}
              aria-pressed={props.layout === layout.id}
              onClick={() => props.onLayoutChange(layout.id)}
              className={cn(
                // Coarse pointers get a real touch target; the compact h-6 pill
                // is the established desktop view-switcher size.
                "inline-flex min-h-touch items-center gap-1.5 rounded-md px-2 text-xs font-medium transition-colors sm:h-6 sm:min-h-0",
                props.layout === layout.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              <layout.Icon className="size-3.5" />
              <span className="hidden sm:inline">{layout.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <ProjectTaskSurfaceBody
          view={props.layout}
          loading={props.board.loading}
          error={props.board.error}
          viewProps={props.viewProps}
          onMoveTask={props.board.moveTask}
          // Phase 2: the sidebar board reorders and moves like the project
          // board. Both flags now follow workflow authority rather than being
          // pinned on, so the only thing that disables them is CMMD saying the
          // workflow is unavailable.
          reorderingDisabled={props.board.workflowMutationBlocked}
          mutationDisabled={props.board.workflowMutationBlocked}
          onRetry={() => void props.board.reload()}
        />
      </div>
    </div>
  );
}
